/**
 * The catalogue toolbar's Views controls (2.K deliverable 10): the Views menu, Save view and, on
 * a view page, Update view. Registered in the `catalogue.toolbar` app slot, which takes no props,
 * so the context comes from the router.
 */
import type { SavedViewRequestLayout, SavedViewV1 } from '@codex/contracts';
import { Button, Icon, useLayer } from '@codex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';
import { type KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useTypes } from '../catalogue/data/queries.js';
import { resolveColumns } from '../catalogue/model/columns.js';
import { readView } from '../catalogue/model/view.js';
import { useNotify } from '../shell/notices.js';
import { useGoTo } from '../shell/open-doc.js';
import {
  isConflict,
  refusalIssue,
  useSavedView,
  useSavedViews,
  useViewMutations,
  viewsKey,
} from './data/index.js';
import { type SaveInput, SaveViewDialog } from './dialogs.js';
import { viewHref } from './href.js';
import { CONFLICT_COPY, SAVE_GENERIC, saveIssueCopy } from './model/copy.js';
import {
  isDirty,
  type ToolbarContext,
  toolbarContext,
  toSavedPayload,
  workingView,
} from './model/view-state.js';

export const SAVED = 'View saved.';
export const UPDATED = 'View updated.';
export const NO_CHANGES = 'No changes to save';
export const NO_VIEWS = 'No saved views yet.';

export function ViewsToolbar() {
  // Drawn outside a router (a static render of the slots), the toolbar is empty.
  const router = useRouter({ warn: false });
  if (!router) return null;
  return <RoutedToolbar />;
}

function RoutedToolbar() {
  const match = useRouterState({ select: (state) => state.matches.at(-1) });
  const context = toolbarContext(
    match ? { routeId: match.routeId, params: match.params, search: match.search } : null,
  );
  if (context === null) return null;
  return <ToolbarBody context={context} />;
}

function ToolbarBody({ context }: { context: ToolbarContext }) {
  const notify = useNotify();
  const navigate = useNavigate();
  const client = useQueryClient();
  const mutations = useViewMutations();
  const types = useTypes();
  const { view: saved } = useSavedView(context.mode === 'view' ? context.viewId : null);
  const typeKey = context.mode === 'catalogue' ? context.typeKey : (saved?.typeKey ?? null);
  const typeViews = useSavedViews(typeKey ?? undefined, typeKey !== null);
  const type = types.data?.find((candidate) => candidate.key === typeKey);
  const columns = useMemo(
    () => (type && types.data ? resolveColumns(type, types.data) : null),
    [type, types.data],
  );

  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  if (type === undefined || columns === null || typeKey === null) return null;
  if (context.mode === 'view' && saved === null) return null;

  const working =
    context.mode === 'view' && saved
      ? workingView(saved, context.view, columns)
      : readView(context.view, columns).view;
  const dirty = context.mode === 'view' && saved ? isDirty(saved, working, columns) : false;

  const openLayout: SavedViewRequestLayout = saved?.layout === 'list' ? 'list' : 'table';
  const initialName = saved ? `${saved.name} copy` : type.namePlural;

  const save = async (input: SaveInput) => {
    setBusy(true);
    setRefusal(null);
    try {
      const made = await mutations.create({
        name: input.name,
        typeKey,
        layout: input.layout,
        ...toSavedPayload(working, columns),
      } as Parameters<typeof mutations.create>[0]);
      if (input.pin) {
        try {
          await mutations.pin(made.id);
        } catch {
          notify("The view was saved but couldn't be pinned.");
        }
      }
      setSaving(false);
      void navigate({ to: '/views/$viewId', params: { viewId: made.id }, search: {} });
      notify(SAVED);
    } catch (error) {
      const issue = refusalIssue(error);
      setRefusal(issue ? saveIssueCopy(issue.code) : SAVE_GENERIC);
    } finally {
      setBusy(false);
    }
  };

  const update = async () => {
    if (!dirty || saved === null || updating) return;
    setUpdating(true);
    try {
      await mutations.update(saved.id, {
        baseRv: saved.rv,
        ...toSavedPayload(working, columns),
      } as Parameters<typeof mutations.update>[1]);
      void navigate({
        to: '/views/$viewId',
        params: { viewId: saved.id },
        search: {},
        replace: true,
      });
      notify(UPDATED);
    } catch (error) {
      if (isConflict(error)) {
        await client.invalidateQueries({ queryKey: viewsKey });
        notify(CONFLICT_COPY);
      } else {
        const issue = refusalIssue(error);
        notify(issue ? saveIssueCopy(issue.code) : "The view couldn't be updated.");
      }
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <ViewsMenu views={typeViews.data ?? []} currentId={saved?.id ?? null} />
      <Button
        size="sm"
        icon="plus"
        aria-haspopup="dialog"
        data-testid="views.toolbar.save"
        onClick={() => {
          setRefusal(null);
          setSaving(true);
        }}
      >
        {context.mode === 'view' ? 'Save as new view' : 'Save view'}
      </Button>
      {context.mode === 'view' && (
        <Button
          size="sm"
          data-testid="views.toolbar.update"
          aria-disabled={!dirty || undefined}
          title={dirty ? undefined : NO_CHANGES}
          busy={updating}
          onClick={() => void update()}
        >
          Update view
        </Button>
      )}
      <SaveViewDialog
        open={saving}
        initialName={initialName}
        initialLayout={openLayout}
        busy={busy}
        error={refusal}
        onSave={(input) => void save(input)}
        onClose={() => setSaving(false)}
      />
    </>
  );
}

/** The Views button and its menu: own markup on `cx-menu`, a layer for Esc (decision 88 item 2). */
function ViewsMenu({
  views,
  currentId,
}: {
  views: readonly SavedViewV1[];
  currentId: string | null;
}) {
  const goTo = useGoTo();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) trigger.current?.focus();
  };
  useLayer(open, () => close(true), 'menu');

  useEffect(() => {
    if (!open) return;
    const box = trigger.current?.getBoundingClientRect();
    if (box) setAnchor({ top: box.bottom + 4, left: Math.max(8, box.left) });
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const away = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menu.current?.contains(target) || trigger.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    return () => document.removeEventListener('pointerdown', away);
  }, [open]);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const at = list.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      list[(at + step + list.length) % list.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      list[event.key === 'Home' ? 0 : list.length - 1]?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        ref={trigger}
        size="sm"
        icon="stack"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="views.toolbar.list"
        onClick={() => setOpen(!open)}
      >
        Views
      </Button>
      {open && (
        <div
          ref={menu}
          role="menu"
          aria-label="Views"
          data-testid="views.list.menu"
          className="cx-menu cx-menu--popup"
          style={anchor ?? { top: 0, left: 0 }}
          onKeyDown={onKeyDown}
        >
          {views.length === 0 && (
            <div role="menuitem" aria-disabled="true" tabIndex={-1} className="cx-menu__empty">
              {NO_VIEWS}
            </div>
          )}
          {views.map((view) => (
            <a
              key={view.id}
              role="menuitem"
              href={viewHref(view.id)}
              tabIndex={-1}
              className="cx-menu__item"
              aria-current={view.id === currentId ? 'page' : undefined}
              onClick={(event) => {
                goTo(viewHref(view.id), event);
                setOpen(false);
              }}
            >
              <span className="cx-menu__label">{view.name}</span>
              {view.pinnedItemId !== null && <Icon name="pin" size={14} aria-label="Pinned" />}
            </a>
          ))}
        </div>
      )}
    </>
  );
}
